import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik, Field, ErrorMessage, FieldArray } from 'formik';
import { toast } from 'react-toastify';
import MoneyField from '~/Components/Form/MoneyField';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { obterCartao, salvarCartao } from '~/Services/Service';
import { cartaoSchema } from './ModalCartao.schema';
import SecaoTaxasGe from './SecaoTaxasGe';

const valoresIniciais = {
  descricao: '',
  taxa: 0,
  dias: 0,
  taxasPorGe: [],
};

const ModalCartao = ({ show, onHide, cartao, onSalvar }) => {
  const ehEdicao = cartao != null;
  const [initialValues, setInitialValues] = useState(valoresIniciais);
  const [carregando, setCarregando] = useState(false);
  // Fix-round PR#548: confirmação ao cancelar/fechar com diff pendente
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (!show) return;

    if (!ehEdicao) {
      setInitialValues(valoresIniciais);
      return;
    }

    let ativo = true;
    const carregar = async () => {
      setCarregando(true);
      try {
        const resp = await obterCartao(cartao.codigo);
        if (!ativo) return;
        setInitialValues({
          descricao: resp.descricao || '',
          taxa: resp.taxa != null ? resp.taxa : 0,
          dias: resp.dias != null ? resp.dias : 0,
          taxasPorGe: (resp.taxas_por_ge || []).map((ge) => ({
            codigoGrupoEmpresa: ge.codigo_grupo_empresa,
            taxa: ge.taxa,
          })),
        });
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
        onHide();
      } finally {
        if (ativo) setCarregando(false);
      }
    };
    carregar();
    return () => { ativo = false; };
  }, [show, cartao]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        codigo: ehEdicao ? cartao.codigo : null,
        descricao: values.descricao,
        taxa: Number(values.taxa),
        dias: Number(values.dias),
        taxas_por_ge: values.taxasPorGe.map((ge) => ({
          codigo_grupo_empresa: ge.codigoGrupoEmpresa,
          taxa: Number(ge.taxa),
        })),
      };
      await salvarCartao(payload);
      toast.success(ehEdicao ? 'Cartão atualizado com sucesso.' : 'Cartão criado com sucesso.');
      onSalvar({ criado: !ehEdicao });
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Wrapper para tentar fechar; se Formik estiver dirty, abre confirmação.
  // O Formik state vive dentro do <Formik>, então usamos um ref via render prop abaixo.
  const tryClose = (dirty) => {
    if (dirty) {
      setConfirmCancel(true);
    } else {
      onHide();
    }
  };

  const handleConfirmCancel = (confirmado) => {
    setConfirmCancel(false);
    if (confirmado) onHide();
  };

  // Estado de dirty exposto pelo Formik via render prop precisa ser compartilhado
  // com o Modal wrapper (ESC/backdrop). Como o Modal é fechado por backdrop="static"
  // e keyboard padrão, intercepta-se via prop `onHide` do Modal — recebe o último
  // dirty conhecido via ref-like.
  const dirtyRef = React.useRef(false);

  return (
    <>
    <Modal
      show={show}
      onHide={() => tryClose(dirtyRef.current)}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {ehEdicao ? `Editar Cartão — ${cartao.codigo}` : 'Novo Cartão'}
        </Modal.Title>
      </Modal.Header>

      {carregando ? (
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Carregando...</p>
        </Modal.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={cartaoSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, dirty, handleSubmit: formikSubmit }) => {
            dirtyRef.current = dirty;
            return (
            <Form onSubmit={formikSubmit} noValidate>
              <Modal.Body>
                <Row>
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label as="small" className="mb-1 font-weight-bold">
                        Descrição <span className="text-danger">*</span>
                      </Form.Label>
                      <Field name="descricao">
                        {({ field }) => (
                          <Form.Control
                            {...field}
                            size="sm"
                            maxLength={30}
                            isInvalid={touched.descricao && !!errors.descricao}
                            placeholder="Nome da operadora"
                          />
                        )}
                      </Field>
                      <ErrorMessage name="descricao" component={Form.Control.Feedback} type="invalid" />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label as="small" className="mb-1 font-weight-bold">
                        Dias <span className="text-danger">*</span>
                      </Form.Label>
                      <Field name="dias">
                        {({ field }) => (
                          <Form.Control
                            {...field}
                            size="sm"
                            type="number"
                            min={0}
                            step={1}
                            isInvalid={touched.dias && !!errors.dias}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="dias" component={Form.Control.Feedback} type="invalid" />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label as="small" className="mb-1 font-weight-bold">
                        Taxa (%) <span className="text-danger">*</span>
                      </Form.Label>
                      <Field
                        name="taxa"
                        component={MoneyField}
                        className={`form-control form-control-sm${touched.taxa && errors.taxa ? ' is-invalid' : ''}`}
                        prefix=""
                        decimalSeparator=","
                        thousandSeparator="."
                        precision={2}
                        placeholder="0,00"
                      />
                      <ErrorMessage name="taxa" component="small" className="text-danger d-block" />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Round 2 PR#548: PRD/TechSpec descrevem "Taxas por GE" como
                    seção do modo edição. Em criação, o detalhe permanece oculto
                    e fica disponível após salvar e reabrir o cartão. */}
                {ehEdicao && (
                  <>
                    <FieldArray name="taxasPorGe">
                      {(arrayHelpers) => (
                        <SecaoTaxasGe
                          taxasPorGe={values.taxasPorGe}
                          arrayHelpers={arrayHelpers}
                          dirty={dirty}
                          errors={errors.taxasPorGe}
                        />
                      )}
                    </FieldArray>

                    {/* Fix-round PR#548: feedback global do array para erros não-de-linha
                        (limite, unicidade) e tipoString quando errors.taxasPorGe é string. */}
                    {typeof errors.taxasPorGe === 'string' && (
                      <small className="text-danger d-block mt-2">
                        {errors.taxasPorGe}
                      </small>
                    )}
                  </>
                )}
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => tryClose(dirty)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
              </Modal.Footer>
            </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
    <ModalConfirmacao
      show={confirmCancel}
      title="Descartar alterações?"
      message="Você tem alterações não salvas. Deseja realmente descartar e fechar o cadastro?"
      onClose={handleConfirmCancel}
    />
    </>
  );
};

export default ModalCartao;
